import { defineMessages } from '@kyruus/intl';
import { withoutTrailingSlash } from 'Common/urls/util';

// Global site name and alternate names are used for SEO purposes.
// These should be consistent across all pages in the domain.
// See https://developers.google.com/search/docs/appearance/site-names#guidelines.
export const globalSiteNameMessages = defineMessages({
  globalSiteName: {
    id: 'global.site.name',
    description: 'Global site name for SEO purposes',
    defaultMessage: ''
  },
  globalAlternateSiteName: {
    id: 'global.site.alternate_name',
    description:
      'Global alternate site names for SEO purposes, comma-separated',
    defaultMessage: ''
  }
});

/**
 * Splits a comma-separated list of alternate site names.
 *
 * @param {string} [alternateNames] - Comma-separated alternate site names.
 * @returns {Array<string>} Trimmed, non-empty alternate site names.
 */
export function parseAlternateNames(alternateNames) {
  return (alternateNames || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
}

/**
 * Builds a `WebSite` JSON-LD `<script>` tag so Google can pick up the site name.
 * Google requires `name` and `url` (the canonical home page of the domain).
 *
 * @param {Object} params
 * @param {string} params.name - The site name.
 * @param {Array<string>} [params.alternateNames] - Alternate site names.
 * @param {string} params.host - The site's domain, with or without protocol.
 * @returns {string} The script tag, or an empty string if `name` or `host` is missing.
 */
export function buildWebSiteJsonLd({ name, alternateNames = [], host }) {
  if (!name || !host) {
    return '';
  }

  const hostName = withoutTrailingSlash(host).replace(/^https?:\/\//, '');

  const data = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url: `https://${hostName}/`,
    ...(alternateNames.length > 0 ? { alternateName: alternateNames } : {})
  };

  // escape `<` so customer-provided values can't close the script tag early
  const json = JSON.stringify(data).replace(/</g, '\\u003c');

  return `<script type="application/ld+json">${json}</script>`;
}
